import React, { useContext } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import hoc from './hoc';
import { BootstrapModal } from 'shared-components/modal/bootstrap-modal';
import CheckboxField from 'shared-components/reduxForms/fields/CheckboxField/CheckboxField';
import { Button } from '@ps-design-system/components/Button';
import { Text } from '@ps-design-system/components/Text';
import { Divider } from '@ps-design-system/components/Divider';

import { tocByUseType } from '../data/utils';
import { StatsigContext } from '../../elements-statsig-provider/statsig-context';

const DeleteListingModal = ({
  isChecked,
  toggleChecked,
  deleteListing,
  showSpinner,
  errorMessage,
  listingData,
  intl,
  ...props
}) => {
  const {
      featureGates
    } = useContext(StatsigContext);

  const isEnabled = featureGates.pause_listing ?? false;

  const useTypeData = tocByUseType[get(listingData, 'use_type')];
  const modalTitle = get(useTypeData, 'modalTitle', '');

  return (
    <BootstrapModal
      {...props}
      header={
        <Text typeface="headline-3-b" >
          {
            modalTitle
              ? <FormattedMessage id="deactivate.modal.deactivate_useType_listing_heading" defaultMessage="Deactivate {useType} listing" values={{ useType: intl.formatMessage(modalTitle) }}/>
              : <FormattedMessage id="deactivate.modal.deactivate_listing_heading" defaultMessage="Deactivate listing"/>
          }
        </Text>
      }
      body={
        <div>
          <div className="margin-xl-bottom">
            <Text typeface="body-2-m" >
              <FormattedMessage id="deactivate.modal.confirmation" defaultMessage="Are you sure you want to deactivate this listing?"/>
            </Text>
          </div>
          <div className="flex start">
            <div className="margin-md-right">
              <CheckboxField
                id="deleteListing"
                label={<FormattedMessage id="hosting.delete_listing_modal.delete_listing_checkbox_label" defaultMessage="I understand that this will delete all associated information with this listing"/>}
                input={{
                  onChange: toggleChecked
                }}
              />
            </div>
          </div>
        </div>
      }
      modalSize="sm-size"
      modalClass="delete-listing-modal"
      footer={
        <div className='deactivate-footer'>
          <Divider spacing={0} className='divider' />
          <div className='deactivate-footer deactivate-container'>
            {
              isEnabled && (
                <div className='description tw-text-gray-600'>
                  <Text typeface="body-2" >
                    <FormattedMessage id="deactivate.modal.renovating" defaultMessage="Renovating your space or heading out on a long trip? Whatever the reason, if you need to temporarily stop bookings, consider pausing your listing. It stays visible but won't be available to book."/>
                    {' '}
                    <a className='link tw-text-gray-600' href="https://support.peerspace.com/en/articles/13048478-pause-listing-feature" target="_blank" rel="noopener noreferrer"><FormattedMessage id="uploader.three_d_tour_modal.description_link" defaultMessage="Learn more" /></a>
                  </Text>
                </div>
              )
            }
            <div className='deactivate-button'>
              <Button
                onClick={deleteListing}
                disabled={!isChecked}
                appearance="destructive"
                fullWidth={true}
              >
                <FormattedMessage id="deactivate.modal.deactivate" defaultMessage="Deactivate"/>
              </Button>
            </div>
            <div className='cancel-button'>
              <Button
                onClick={() => props.toggleModal('') }
                variant="underline"
              >
                <FormattedMessage id="hosting.delete_listing_modal.button_text_cancel" defaultMessage="Cancel"/>
              </Button>
            </div>
            <div className="margin-xl-top text-red text-center">{errorMessage}</div>
          </div>
        </div>
      }
    />
  );
};

DeleteListingModal.propTypes = {
  cancelAction: PropTypes.func,
  listingData: PropTypes.object,
  bodyText: PropTypes.string,
  isChecked: PropTypes.bool,
  toggleChecked: PropTypes.func,
  toggleModal: PropTypes.func,
  deleteListing: PropTypes.func,
  showSpinner: PropTypes.bool,
  errorMessage: PropTypes.string,
  intl: intlShape
};

export default hoc(injectIntl(DeleteListingModal));
